import React from 'react';
import {
  Card, Col, Container, Row, Spinner,
} from 'react-bootstrap';
import Accordion from 'components/Accordion';
import useClassicComparatorModel from 'utils/hooks/meteorology/classicComparator/useClassicComparatorModel';
import DatePicker from 'components/DatePicker';
import buildAccordionClickFunction from 'helper/buildAccordionClickFunction';
import TitleSemiBold from 'components/Typography/TitleSemiBold';
import RadioButton from 'components/DesignSystem/RadioButton';
import { cssVar } from 'constants/theme';

export enum ModelType {
  BASE = 'base',
  CONFRONTATIONAL = 'confrontational',
}

const sectionTitleStyle = {
  color: cssVar('text-disabled'),
  fontSize: '18px',
  fontWeight: '600',
};

function ModelRadioButton({
  value,
  label,
  modelType,
  model,
  selectModel,
  closeAccordion,
}: {
  value: string;
  label: string;
  modelType: ModelType;
  model: string;
  selectModel: (value: string) => void;
  closeAccordion: () => void;
}) {
  const handleModel = () => {
    closeAccordion();
    selectModel(value);
  };

  return (
    <RadioButton
      id={`model-${value}-${modelType}`}
      className="text-uppercase pe-1"
      label={label}
      value={value}
      onChange={handleModel}
      checked={model === value}
    />
  );
}

export default function ModelAccordion({
  modelType,
  title,
  model,
  setModel,
  dateDiff,
  setDateDiff,
  runtime,
  setRuntime,
  member,
  setMember,
  rmv,
  setRMV,
  id,
}: {
  modelType: ModelType;
  title: string;
  model: string;
  setModel: (value: string) => void;
  dateDiff: string;
  setDateDiff: (value: string | undefined) => void;
  runtime: number | undefined;
  setRuntime: (value: number | undefined) => void;
  member: string | undefined;
  setMember: (value: string | undefined) => void;
  rmv: boolean;
  setRMV: (value: boolean) => void;
  id: string;
}) {
  const closeDateAccordion = buildAccordionClickFunction(`${id}-date-options-accordion`);
  const closeRuntimeAccordion = buildAccordionClickFunction(`${id}-runtime-options-accordion`);
  const closeMemberAccordion = buildAccordionClickFunction(`${id}-member-options-accordion`);
  const closeRMVAccordion = buildAccordionClickFunction(`${id}-rmv-options-accordion`);

  const {
    isFetching,
    modelOptions,
    availableIntervals,
    runtimeOptions,
    memberOptions,
    date,
    setDate,
    selectModel,
  } = useClassicComparatorModel({
    model,
    setModel,
    dateDiff,
    setDateDiff,
    runtime,
    setRuntime,
    member,
    setMember,
    isBaseModel: modelType === ModelType.BASE,
  });

  const handleDate = (value: Date | null) => {
    setDate(value);
    closeDateAccordion();
  };

  if (isFetching) {
    return (
      <div className="text-center mb-1">
        <Spinner />
      </div>
    );
  }

  return (
    <Container id={id}>
      <Row className="mt-4">
        <Col className="d-inline-flex justify-content-center">
          <p style={sectionTitleStyle}>{title}</p>
        </Col>
      </Row>
      <Accordion defaultActiveKey="1">
        <Accordion.Item
          eventKey="3"
          id={`${id}-model-options-accordion`}
        >
          <Accordion.Header><TitleSemiBold>Modelo</TitleSemiBold></Accordion.Header>
          <Accordion.Body>
            <div>
              {modelOptions.map((m) => (
                <ModelRadioButton
                  key={`modelRadioButton-${m.value}-${modelType}`}
                  modelType={modelType}
                  value={m.value}
                  label={m.label}
                  model={model}
                  selectModel={selectModel}
                  closeAccordion={buildAccordionClickFunction(`${id}-model-options-accordion`)}
                />
              ))}
            </div>
          </Accordion.Body>
        </Accordion.Item>
        <Accordion.Item
          eventKey="4"
          id={`${id}-date-options-accordion`}
        >
          <Accordion.Header><TitleSemiBold>Data de Previsão</TitleSemiBold></Accordion.Header>
          <Accordion.Body className="d-grid">
            <DatePicker
              inline
              showMonthDropdown
              showYearDropdown
              selected={date}
              onChange={handleDate}
              includeDateIntervals={availableIntervals}
            />
          </Accordion.Body>
        </Accordion.Item>
        <Accordion.Item
          eventKey="5"
          id={`${id}-runtime-options-accordion`}
        >
          <Accordion.Header><TitleSemiBold>Runtime</TitleSemiBold></Accordion.Header>
          <Accordion.Body className="d-grid">
            {runtimeOptions.map((ro) => (
              <RadioButton
                key={ro.id}
                id={`runtime-${ro.id}-${modelType}`}
                label={ro.label}
                value={ro.id}
                checked={runtime === ro.id}
                onChange={() => {
                  setRuntime(ro.id);
                  closeRuntimeAccordion();
                }}
              />
            ))}
          </Accordion.Body>
        </Accordion.Item>
        <Accordion.Item
          eventKey="6"
          id={`${id}-member-options-accordion`}
        >
          <Accordion.Header><TitleSemiBold>Membro</TitleSemiBold></Accordion.Header>
          <Accordion.Body className="d-grid">
            {memberOptions.map((mo) => (
              <RadioButton
                key={mo.id}
                id={`member-${mo.id}-${modelType}`}
                label={mo.label}
                value={mo.id}
                checked={member === mo.id}
                onChange={() => {
                  setMember(mo.id);
                  closeMemberAccordion();
                }}
              />
            ))}
          </Accordion.Body>
        </Accordion.Item>
        <Accordion.Item
          eventKey="7"
          id={`${id}-rmv-options-accordion`}
        >
          <Accordion.Header><TitleSemiBold>RMV</TitleSemiBold></Accordion.Header>
          <Accordion.Body>
            <Card.Body>
              <RadioButton
                id={`withRMV-${modelType}`}
                label="Com RMV"
                value={`withRMV-${modelType}`}
                checked={rmv}
                onChange={() => {
                  setRMV(true);
                  closeRMVAccordion();
                }}
              />
              <RadioButton
                id={`withoutRMV-${modelType}`}
                label="Sem RMV"
                value={`withoutRMV-${modelType}`}
                checked={!rmv}
                onChange={() => {
                  setRMV(false);
                  closeRMVAccordion();
                }}
              />
            </Card.Body>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
    </Container>
  );
}
